import * as React from "react";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import Button from "@amzn/awsui-components-react/polaris/button";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Select, { SelectProps } from "@amzn/awsui-components-react/polaris/select";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import Flashbar from "@amzn/awsui-components-react/polaris/flashbar";
import Box from "@amzn/awsui-components-react/polaris/box";
import { API } from "aws-amplify";
import { PrescriptionOption } from "./ConsumptionGuardOverridePage";

interface Props {
  profileId: string;
  prescriptions: PrescriptionOption[];
  selectedDrugAsin: string;
  onOrderSuccess: () => void;
}

interface Address {
  legacyAddressId: string;
  mailingAddress: string;
}

interface State {
  personId: string;
  customerId: string;
  isLoadingJourney: boolean;
  journeyError: string | null;
  addresses: Address[];
  selectedAddressId: string;
  isLoadingAddresses: boolean;
  orderType: string;
  isSubmitting: boolean;
  orderResult: any | null;
  error: string | null;
}

const ORDER_TYPE_OPTIONS: SelectProps.Option[] = [
  { label: "Cash Order", value: "cash" },
  { label: "PrimeRx Order", value: "primeRx" },
  { label: "Insurance Order", value: "insurance" },
  { label: "RxPass (Arrow) Order", value: "arrow" },
];

class PlaceOrderSection extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      personId: "",
      customerId: "",
      isLoadingJourney: true,
      journeyError: null,
      addresses: [],
      selectedAddressId: "",
      isLoadingAddresses: false,
      orderType: "cash",
      isSubmitting: false,
      orderResult: null,
      error: null,
    };
  }

  componentDidMount() {
    this.resolveCustomerJourney();
  }

  private resolveCustomerJourney = async () => {
    const { profileId } = this.props;
    this.setState({ isLoadingJourney: true, journeyError: null });

    try {
      const response = await API.get("ApaqPortalAPI", `/customer-journey/${encodeURIComponent(profileId)}`, {});
      const personId = response.personId || "";

      if (!personId) {
        this.setState({ isLoadingJourney: false, journeyError: "Could not resolve personId from profileId." });
        return;
      }

      this.setState({ personId, customerId: response.customerId || "", isLoadingJourney: false });
      this.fetchAddresses(personId);
    } catch (e) {
      const err = e as any;
      const message = err?.response?.data?.message || err?.message || "Failed to resolve customer journey";
      this.setState({ isLoadingJourney: false, journeyError: message });
    }
  };

  private fetchAddresses = async (personId: string) => {
    this.setState({ isLoadingAddresses: true });

    try {
      const response = await API.get("ApaqPortalAPI", `/customer/address/get-addresses?personId=${encodeURIComponent(personId)}`, {});
      const activeAddresses: Address[] = (response.customerAddresses || [])
        .filter((a: any) => a.active)
        .map((a: any) => ({ legacyAddressId: a.legacyAddressId, mailingAddress: a.mailingAddress || a.legacyAddressId }));

      this.setState({
        addresses: activeAddresses,
        selectedAddressId: activeAddresses.length > 0 ? activeAddresses[0].legacyAddressId : "",
        isLoadingAddresses: false,
      });
    } catch (e) {
      const err = e as any;
      const message = err?.response?.data?.message || err?.message || "Failed to fetch addresses";
      this.setState({ isLoadingAddresses: false, error: message });
    }
  };

  private handleSubmit = async () => {
    if (this.state.isSubmitting) return;

    const { prescriptions, selectedDrugAsin } = this.props;
    const { personId, selectedAddressId, orderType } = this.state;

    const prescriptionIds = prescriptions
      .filter((p) => p.drugAsin === selectedDrugAsin)
      .map((p) => p.prescriptionId);

    this.setState({ isSubmitting: true, error: null, orderResult: null });

    try {
      const response = await API.post("ApaqPortalAPI", "/pharmacy-customer/update/create-order", {
        body: {
          personId,
          addressId: selectedAddressId,
          prescriptionIds,
          orderType,
        },
      });
      this.setState({ isSubmitting: false, orderResult: response });
      this.props.onOrderSuccess();
    } catch (e) {
      const err = e as any;
      const message = err?.response?.data?.message || err?.message || "Failed to create order";
      this.setState({ isSubmitting: false, error: message });
    }
  };

  render() {
    const { selectedDrugAsin } = this.props;
    const { isLoadingJourney, journeyError, personId, addresses, selectedAddressId, isLoadingAddresses, orderType, isSubmitting, orderResult, error } = this.state;

    if (isLoadingJourney) {
      return (
        <Container header={<Header variant="h3">Place Order</Header>}>
          <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            <Spinner size="normal" />
            <Box>Resolving customer details...</Box>
          </SpaceBetween>
        </Container>
      );
    }

    if (journeyError) {
      return (
        <Container header={<Header variant="h3">Place Order</Header>}>
          <Flashbar items={[{ type: "error", content: `No customer found for this profile ID. Ensure the profile exists in the current environment (alpha/beta). Details: ${journeyError}` }]} />
        </Container>
      );
    }

    const selectedOrderType = ORDER_TYPE_OPTIONS.find((o) => o.value === orderType) || null;
    const addressOptions: SelectProps.Option[] = addresses.map((a) => ({ label: a.mailingAddress, value: a.legacyAddressId }));
    const selectedAddress = addressOptions.find((o) => o.value === selectedAddressId) || null;
    const canSubmit = !!selectedAddressId && !!personId;

    return (
      <Container header={
        <Header
          variant="h3"
          description={`Person ID: ${personId}`}
          actions={
            <Button
              variant="primary"
              onClick={this.handleSubmit}
              loading={isSubmitting}
              disabled={!canSubmit}
            >
              Place Order
            </Button>
          }
        >
          Place Order - {selectedDrugAsin}
        </Header>
      }>
        <SpaceBetween size="l">
          {error && (
            <Flashbar items={[{ type: "error", content: error, dismissible: true, onDismiss: () => this.setState({ error: null }) }]} />
          )}

          {orderResult && (
            <div>
              <Flashbar items={[{ type: "success", content: "Order placed successfully. There may be a slight delay before the override is consumed. Use the refresh button on the Existing Overrides table to validate the override status." }]} />
              <pre style={{
                backgroundColor: "#f5f5f5",
                border: "1px solid lightgray",
                borderRadius: "4px",
                padding: "10px",
                overflowX: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "normal",
                marginTop: "12px",
              }}>
                {JSON.stringify(orderResult, null, 2)}
              </pre>
            </div>
          )}

          <FormField label="Order Type">
            <Select
              selectedOption={selectedOrderType}
              onChange={({ detail }) => this.setState({ orderType: detail.selectedOption.value || "cash" })}
              options={ORDER_TYPE_OPTIONS}
            />
          </FormField>

          <FormField label="Shipping Address">
            {isLoadingAddresses ? (
              <Spinner size="normal" />
            ) : addresses.length === 0 ? (
              <Box color="text-body-secondary">No active addresses found.</Box>
            ) : (
              <Select
                selectedOption={selectedAddress}
                onChange={({ detail }) => this.setState({ selectedAddressId: detail.selectedOption.value || "" })}
                options={addressOptions}
              />
            )}
          </FormField>
        </SpaceBetween>
      </Container>
    );
  }
}

export default PlaceOrderSection;
